import { useLayers, useOpenLayersContext } from '@ais-ui/react-openlayers';
import { getTargetInfoAtPixel, useSession } from '@ais-ui/websockets';
import { MapBrowserEvent } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAISMapContext } from './AISMapContext';
import { useViewMode, ViewMode } from '../../components/ViewModeProvider';
import { useSelectionState } from '../../components/SelectionStateProvider';

export function useClickOnMap(enabled = true) {
  const [clickPosition, setClickPosition] = useState<Coordinate | null>(null);
  const [isCtrlClick, setIsCtrlClick] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { setIsClickingShip } = useAISMapContext();
  const { map } = useOpenLayersContext();
  const { isShipLayerVisible } = useLayers();

  useEffect(() => {
    const onClick = (e: MapBrowserEvent<UIEvent>) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Cannot click ships if the ship layer is not visible
      if (!isShipLayerVisible) {
        return;
      }

      // Immediately disable hover to prevent interference
      setIsClickingShip(true);

      // Check if Ctrl key is pressed during the click
      const originalEvent = e.originalEvent as MouseEvent;
      const ctrlPressed = originalEvent.ctrlKey;
      setIsCtrlClick(ctrlPressed);
      setClickPosition(e.coordinate);

      // Shorter timeout - just enough to prevent hover interference during click processing
      timeoutRef.current = setTimeout(() => {
        setIsClickingShip(false);
      }, 500); // Reduced from 2000ms
    };

    if (enabled) {
      map.on('click', onClick);
    } else {
      map.un('click', onClick);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      map.un('click', onClick);
    };
  }, [enabled, map, setIsClickingShip, isShipLayerVisible]);

  return { clickPosition, setClickPosition, isCtrlClick };
}

export function ListenForShipClicks() {
  const { clickPosition, isCtrlClick } = useClickOnMap();
  const { setTrigger } = useAISMapContext();
  const { setTargetInfoDetailsList } = useSelectionState();
  const { viewMode } = useViewMode();
  const { olSession, replaySession } = useSession();
  const { map } = useOpenLayersContext();
  const [isProcessingClick, setIsProcessingClick] = useState(false);
  const lastProcessedClick = useRef<Coordinate | null>(null);

  const session =
    viewMode === ViewMode.Replay && replaySession ? replaySession : olSession;

  useEffect(() => {
    if (!clickPosition || isProcessingClick) {
      return;
    }

    // Check if we've already processed this exact click position
    if (
      lastProcessedClick.current &&
      lastProcessedClick.current[0] === clickPosition[0] &&
      lastProcessedClick.current[1] === clickPosition[1]
    ) {
      return;
    }

    const processClick = async () => {
      setIsProcessingClick(true);
      lastProcessedClick.current = clickPosition;

      try {
        // Convert coordinate to pixels immediately when processing click
        const [xFloat, yFloat] = map.getPixelFromCoordinate(clickPosition);
        const pixelCoordinate = {
          x: Math.round(xFloat),
          y: Math.round(yFloat),
        };

        // Direct API call - no cache interference
        const targetInfoFromClick = await getTargetInfoAtPixel(
          session,
          pixelCoordinate.x,
          pixelCoordinate.y,
          true, // selectTarget
          !isCtrlClick // deselectOtherTargets - Shows the track on multiple targets if clicking with Ctrl
        );

        if (
          !targetInfoFromClick ||
          targetInfoFromClick.extractedTargetInfo.length === 0
        ) {
          // No target found at click position
          if (isCtrlClick) {
            // Keep existing selection for Ctrl+click on empty space
            setTargetInfoDetailsList((prevList) => prevList);
          } else {
            // Clear selection for regular click on empty space
            setTargetInfoDetailsList([]);
          }
        } else {
          // Target found
          if (isCtrlClick) {
            setTargetInfoDetailsList((prevList) => {
              // Check if mmsi exists, else use methodMmsi
              const newMmsi =
                (targetInfoFromClick.extractedTargetInfo[0] as any)?.mmsi ||
                (targetInfoFromClick.extractedTargetInfo[0] as any)?.methydMmsi;

              // Filter out any item that has the same MMSI or methodMmsi as the new one
              const filteredList = prevList.filter(
                (item) =>
                  ((item.extractedTargetInfo[0] as any)?.mmsi ||
                    (item.extractedTargetInfo[0] as any)?.methydMmsi) !==
                  newMmsi
              );

              // Return the new list with the targetInfoFromClick added
              return [targetInfoFromClick, ...filteredList];
            });
          } else {
            // Regular click - replace selection
            setTargetInfoDetailsList([targetInfoFromClick]);
          }
        }

        // Trigger update after processing
        setTimeout(() => {
          setTrigger((prevTrigger) => prevTrigger + 1);
        }, 60);
      } catch (error) {
        console.error('Error processing ship click:', error);
        // Handle error case - maybe clear selection or show error message
      } finally {
        setIsProcessingClick(false);
      }
    };

    processClick();
  }, [clickPosition, isCtrlClick, session, map]);

  return null;
}
